import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { OrganizationSchema, WebPageSchema } from '@/components/feature/SchemaOrg';


const aboutBreadcrumb = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

const brandStats = [
  { number: '500+', label: '精选韩漫' },
  { number: '8', label: '内容分类' },
  { number: '99%', label: '用户好评' },
  { number: '3年+', label: '运营时长' },
];

const features = [
  {
    icon: 'ri-bookmark-line',
    title: '精选优质内容',
    desc: '专业编辑团队精心筛选每一部韩漫作品，确保推荐的漫画都具有高品质的画风和故事。',
  },
  {
    icon: 'ri-flashlight-line',
    title: '更新及时同步',
    desc: '紧跟各大韩漫平台更新节奏，确保您获取的推荐信息始终是最新的。热门新作第一时间收录。',
  },
  {
    icon: 'ri-file-list-3-line',
    title: '分类精准导航',
    desc: '恋爱、热血、奇幻、悬疑等多个分类，精准匹配您的阅读偏好，快速找到感兴趣的韩漫。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '无删减体验',
    desc: '推荐的所有韩漫资源均为原版无删减内容，提供下拉式沉浸阅读体验，保证内容完整性。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '读者至上',
    desc: '以读者需求为核心，持续优化推荐算法和用户体验。您的每次反馈都是我们改进的动力。',
  },
  {
    icon: 'ri-global-line',
    title: '免费开放',
    desc: '所有推荐资源均支持免费阅读，无任何隐藏收费。让每一位韩漫爱好者都能畅享阅读乐趣。',
  },
];

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const seoTitle = '关于我们_免费韩漫在线漫画推荐_韩漫漫画网站';
  const seoDesc = '韩漫漫画网站致力于为韩漫爱好者提供优质的免费韩漫在线漫画推荐服务。了解我们的品牌故事、服务特色和联系方式。';
  const seoKeywords = '关于韩漫漫画,免费韩漫推荐,在线漫画网站,韩漫阅读平台';

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        canonicalUrl="/about"
      />
      <OrganizationSchema />
      <WebPageSchema
        title={seoTitle}
        description={seoDesc}
        url="/about"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <Breadcrumb items={aboutBreadcrumb} />

            {/* Page Title */}
            <div className="py-10 text-center">
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
                韩漫漫画网站 - 您的一站式免费韩漫在线推荐平台
              </p>
            </div>

            {/* Brand Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-16">
              {brandStats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-100 rounded-lg p-6 text-center"
                >
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">
                    {stat.number}
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Brand Introduction */}
            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6 text-center">
                品牌介绍
              </h2>
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-4">
                  <p>
                    <strong>韩漫漫画网站</strong>（hanmancomic.com）成立于 2023 年，是一个专注于免费韩漫在线推荐的综合性内容平台。我们致力于为广大韩漫爱好者提供<strong>最全面、最及时、最优质的韩漫推荐服务</strong>。
                  </p>
                  <p>
                    在韩漫产业蓬勃发展的今天，越来越多的优秀作品涌现，但读者寻找优质漫画的时间和成本也在增加。韩漫漫画网站的诞生，正是为了解决这一痛点——我们通过专业的编辑团队和精准的推荐系统，帮助读者在海量内容中快速找到心仪的作品。
                  </p>
                  <p>
                    我们推荐的所有韩漫资源均支持<strong>无删减下拉式免费阅读</strong>，涵盖恋爱、热血动作、奇幻冒险、悬疑惊悚、轻松搞笑、都市剧情、BL 专区、古风穿越等多元类型。无论是韩漫资深粉丝还是新入坑的读者，都能在这里找到适合自己的作品。
                  </p>
                  <p>
                    截至目前，韩漫漫画网站已收录超过 <strong>500 部优质韩漫</strong>，累计服务超过百万韩漫爱好者。我们的推荐内容每月保持高频更新，确保读者始终能获取最新的韩漫动态和优质推荐。
                  </p>
                </div>
              </div>
            </section>

            {/* Features */}
            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6 text-center">
                为什么选择我们
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {features.map((feature) => (
                  <div
                    key={feature.title}
                    className="bg-background-100 rounded-lg p-5 hover:bg-background-200/70 transition-colors"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                      <i className={`${feature.icon} text-lg`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      {feature.desc}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Testimonials */}
            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6 text-center">
                读者口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-background-100 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-amber-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                    "作为韩漫新人，这个网站帮了我大忙！分类清晰，推荐精准，我三天就找到了五部超好看的韩漫。无删减下拉式阅读体验也很赞！"
                  </p>
                  <div className="text-xs text-foreground-500">— 小雅，韩漫爱好者</div>
                </div>
                <div className="bg-background-100 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-amber-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                    "推荐质量很高，每部漫画都有详细的介绍和评分，不用担心踩雷。更新也很及时，热门新作上线就能看到推荐文章。"
                  </p>
                  <div className="text-xs text-foreground-500">— 阿杰，漫画博主</div>
                </div>
                <div className="bg-background-100 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-amber-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                    "收藏了这个网站之后，再也不用到处找韩漫推荐了。分类齐全，从恋爱到热血都有覆盖，满足了我各种口味的需求！"
                  </p>
                  <div className="text-xs text-foreground-500">— 小陈，资深读者</div>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  <h3 className="text-lg font-semibold text-foreground-900 mb-4">联系方式</h3>
                  <ul className="space-y-4 text-sm">
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <div className="font-medium text-foreground-800">电子邮箱</div>
                        <div className="text-foreground-500">contact@hanmancomic.com</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <div>
                        <div className="font-medium text-foreground-800">公司地址</div>
                        <div className="text-foreground-500">韩国首尔特别市江南区驿三洞</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-time-line"></i>
                      </div>
                      <div>
                        <div className="font-medium text-foreground-800">工作时间</div>
                        <div className="text-foreground-500">周一至周五 09:00 - 18:00（韩国时间）</div>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  <h3 className="text-lg font-semibold text-foreground-900 mb-4">给我们留言</h3>
                  <form
                    id="about-contact-form"
                    data-readdy-form
                    action="https://readdy.ai/api/form/d9p10b8senbniolesno0"
                    method="POST"
                    className="space-y-4"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                      if (honeypot) {
                        alert('留言已提交，感谢您的反馈！');
                        form.reset();
                        return;
                      }
                      const formData = new FormData(form);
                      fetch(form.action, { method: 'POST', body: formData })
                        .then(async (res) => {
                          const text = await res.text();
                          let parsed;
                          try { parsed = JSON.parse(text); } catch { parsed = null; }
                          return { ok: res.ok, parsed };
                        })
                        .then(({ ok, parsed }) => {
                          if (ok && parsed?.code === 'OK') {
                            alert('留言已提交，感谢您的反馈！');
                            form.reset();
                          } else {
                            const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
                            alert(msg);
                          }
                        })
                        .catch(() => alert('网络错误，请稍后重试'));
                    }}
                  >
                    <input
                      type="text"
                      name="phone_alt"
                      className="website-alt-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1">
                        您的称呼
                      </label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的称呼"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1">
                        电子邮箱
                      </label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的电子邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1">
                        留言内容
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请留下您的意见或建议（最多500字）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2.5 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
                    >
                      发送留言
                    </button>
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}